import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X, Search } from 'lucide-react';

export interface MultiSelectOption {
  value: string | number;
  label: string;
  hint?: string;
}

interface Props {
  label: string;
  options: MultiSelectOption[];
  values: (string | number)[];
  onChange: (vals: (string | number)[]) => void;
  placeholder?: string;
  maxChips?: number;
  loading?: boolean;
  searchable?: boolean;
  className?: string;
}

/**
 * Generic multi-select dropdown for list page filters.
 * Renders as a single chip-style input that opens a popover with
 * checkbox options + search. Pass the resulting array of values to your API.
 */
export function MultiSelectFilter({ label, options, values, onChange, placeholder = 'Select…', maxChips = 2, loading, searchable = true, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    const onScroll = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = q ? options.filter(o => (o.label + ' ' + (o.hint ?? '')).toLowerCase().includes(q.toLowerCase())) : options;

  const toggle = (v: string | number) => {
    if (values.includes(v)) onChange(values.filter(x => x !== v));
    else onChange([...values, v]);
  };
  const clearAll = (e: React.MouseEvent) => { e.stopPropagation(); onChange([]); };
  const allSel = values.length > 0 && filtered.length > 0 && filtered.every(o => values.includes(o.value));

  return (
    <div className={`relative ${className}`} ref={ref}>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white flex items-center justify-between gap-2 hover:border-slate-400 transition">
        <div className="flex items-center gap-1 flex-wrap min-h-[20px]">
          {values.length === 0 && <span className="text-slate-400">{placeholder}</span>}
          {values.slice(0, maxChips).map(v => {
            const o = options.find(x => x.value === v);
            return (
              <span key={String(v)} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5 rounded">
                {o?.label ?? v}
                <X size={11} className="cursor-pointer hover:text-indigo-900"
                  onClick={(e) => { e.stopPropagation(); toggle(v); }} />
              </span>
            );
          })}
          {values.length > maxChips && (
            <span className="text-xs text-slate-500">+{values.length - maxChips} more</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {values.length > 0 && (
            <X size={14} className="text-slate-400 hover:text-slate-700 cursor-pointer" onClick={clearAll} />
          )}
          <ChevronDown size={14} className={`text-slate-500 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && pos && createPortal(
        <div ref={popRef} className="fixed z-[10050] bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-hidden" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {searchable && (
            <div className="p-2 border-b border-slate-100 flex items-center gap-2">
              <Search size={13} className="text-slate-400" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                autoFocus placeholder="Search…"
                className="flex-1 outline-none text-xs"
              />
            </div>
          )}
          <div className="overflow-y-auto max-h-56">
            {loading && <div className="p-3 text-center text-xs text-slate-400">Loading…</div>}
            {!loading && filtered.length === 0 && <div className="p-3 text-center text-xs text-slate-400">No options</div>}
            {!loading && filtered.length > 0 && (
              <button
                onClick={() => onChange(allSel ? values.filter(v => !filtered.some(o => o.value === v)) : Array.from(new Set([...values, ...filtered.map(o => o.value)])))}
                className="w-full text-left px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-50 border-b border-slate-100">
                {allSel ? 'Deselect all visible' : 'Select all visible'}
              </button>
            )}
            {filtered.map(o => {
              const checked = values.includes(o.value);
              return (
                <button key={String(o.value)} onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 ${checked ? 'bg-indigo-50/50' : ''}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300'}`}>
                    {checked && <Check size={11} />}
                  </div>
                  <span className="flex-1 text-left">{o.label}</span>
                  {o.hint && <span className="text-xs text-slate-400">{o.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
